import { FC, useState, useCallback, useMemo } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { AppRoutes } from "@/routes/config";
import { TOption } from "@/types/common";
import { text } from "@/theme/colors";
import { AnnualSummaryCards } from "@/features/dashboard/components/performance-detail/AnnualSummaryCards";
import { AnnualStatTable } from "@/features/dashboard/components/performance-detail/AnnualStatTable";
import { PerformanceDetailHeader } from "@/features/dashboard/components/performance-detail/PerformanceDetailHeader";
import { YEAR_FORMAT } from "@/constants/datetime";
import { Switch } from "@/components/elements/Switch";
import { IconFileExport } from "@/assets/icons";
import { toast } from "@/utils/toast";
import { CSV_EXPORT_FAILED_MESSAGE } from "@/constants/common";
import {
  usePerformanceDetailSummary,
  usePerformanceDetailMatching,
  usePerformanceDetailStaff,
  usePerformanceDetailIndustry,
  usePerformanceDetailPartner,
  usePerformanceDetailClientType,
  exportPerformanceDetailCsvApi,
} from "@/api/services/main/dashboard";
import { getPerformanceDetailPeriod } from "@/features/dashboard/utils/get-performance-detail-period";
import { CONVERSION_RATE_LABELS } from "@/features/dashboard/constants/conversion-rate-labels";

const MONTH_OPTIONS: TOption[] = Array.from({ length: 12 }, (_, i) => ({
  label: `${i + 1}月`,
  value: String(i + 1),
}));

// Values match BE fiscal-year.helper: STATUS_NEW_TALENT = -2, ECaseMatchStatus.BEFORE_PROPOSAL = 0
const STATUS_OPTIONS: TOption[] = [
  { label: "新規登録人材", value: "-2" },
  { label: "推薦前", value: "0" },
  { label: "推薦中", value: "1" },
  { label: "選考中", value: "2" },
  { label: "内定", value: "3" },
  { label: "内定受諾", value: "4" },
];

export const PerformanceDetailPage: FC = () => {
  const navigate = useNavigate();
  const [targetYear, setTargetYear] = useState<Dayjs>(dayjs());
  const [targetMonth, setTargetMonth] = useState("3");
  const [staffStatus, setStaffStatus] = useState("4");
  const [industryStatus, setIndustryStatus] = useState("4");
  const [partnerStatus, setPartnerStatus] = useState("4");
  const [clientTypeStatus, setClientTypeStatus] = useState("4");
  const [showConversionRate, setShowConversionRate] = useState(false);

  const baseParams = {
    targetYear: targetYear.format("YYYY"),
    targetMonth,
  };

  const { data: summaryRes, isLoading: summaryLoading } = usePerformanceDetailSummary(baseParams);
  const { data: matchingRes, isLoading: matchingLoading } = usePerformanceDetailMatching(baseParams);
  const { data: staffRes, isLoading: staffLoading } = usePerformanceDetailStaff({ ...baseParams, status: staffStatus });
  const { data: industryRes, isLoading: industryLoading } = usePerformanceDetailIndustry({ ...baseParams, status: industryStatus });
  const { data: partnerRes, isLoading: partnerLoading } = usePerformanceDetailPartner({ ...baseParams, status: partnerStatus });
  const { data: clientTypeRes, isLoading: clientTypeLoading } = usePerformanceDetailClientType({ ...baseParams, status: clientTypeStatus });

  const handleYearChange = useCallback((year: Dayjs | null) => {
    if (year) setTargetYear(year);
  }, []);

  const handleMonthChange = useCallback((value: string) => {
    setTargetMonth(value);
  }, []);

  const matchingRows = useMemo(() => {
    const rows = matchingRes?.data?.rows ?? [];
    if (showConversionRate) return rows;
    return rows.filter((row) => !CONVERSION_RATE_LABELS.includes(row.label));
  }, [matchingRes?.data?.rows, showConversionRate]);

  const [isExporting, setIsExporting] = useState(false);

  // Exports the full 年間実績表 (all 6 集計範囲 blocks) — the BE builds every block regardless of
  // the on-screen 集計範囲 dropdowns, so the CSV is not limited to the currently selected status.
  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportPerformanceDetailCsvApi(targetYear.format("YYYY"), targetMonth, showConversionRate);
    } catch {
      toast.error(CSV_EXPORT_FAILED_MESSAGE);
    } finally {
      setIsExporting(false);
    }
  }, [targetYear, targetMonth, showConversionRate]);

  const period = useMemo(() => getPerformanceDetailPeriod(Number(targetYear.format(YEAR_FORMAT)), Number(targetMonth)), [targetYear, targetMonth]);
  const yearLabel = `${period.startYear}年`;

  return (
    <>
    <Stack gap={2} sx={{ minHeight: "calc(100vh - 194px)" }}>
        <TopPage title="実績詳細" enablePageBack={false} />

      <InnerBox
        title={
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ width: "100%" }}>
            <Typography variant="head20Bold" color={text.primary}>
              指定年度の実績値
            </Typography>
            <PerformanceDetailHeader
              targetYear={targetYear}
              onYearChange={handleYearChange}
              categoryOptions={MONTH_OPTIONS}
              selectedCategory={targetMonth}
              onCategoryChange={handleMonthChange}
              periodLabel={period.rangeLabel}
            />
          </Stack>
        }
        bordered={true}
        showDivider={false}
      >
        <Stack gap={4} mt="13px">
          <AnnualSummaryCards data={summaryRes?.data} isLoading={summaryLoading} />

          <AnnualStatTable
            title={`年間マッチング数（${yearLabel}）`}
            firstColumnLabel=""
            rows={matchingRows}
            isLoading={matchingLoading}
            targetMonth={Number(targetMonth)}
            showFooter={false}
            rightContent={
              <SwitchWrapper>
                <Switch label="転換率を表示" checked={showConversionRate} onChange={(e) => setShowConversionRate(e.target.checked)} nonControl />
              </SwitchWrapper>
            }
          />

          <AnnualStatTable
            title={`担当者別年間実績表（${yearLabel}）`}
            firstColumnLabel="担当者"
            rows={staffRes?.data?.rows ?? []}
            isLoading={staffLoading}
            statusOptions={STATUS_OPTIONS}
            selectedStatus={staffStatus}
            onStatusChange={setStaffStatus}
            targetMonth={Number(targetMonth)}
          />

          <AnnualStatTable
            title={`パートナー別年間実績表（${yearLabel}）`}
            firstColumnLabel="パートナー"
            rows={partnerRes?.data?.rows ?? []}
            isLoading={partnerLoading}
            statusOptions={STATUS_OPTIONS}
            selectedStatus={partnerStatus}
            onStatusChange={setPartnerStatus}
            targetMonth={Number(targetMonth)}
          />

          <AnnualStatTable
            title={`区分別年間実績表（${yearLabel}）`}
            firstColumnLabel="区分"
            rows={clientTypeRes?.data?.rows ?? []}
            isLoading={clientTypeLoading}
            statusOptions={STATUS_OPTIONS}
            selectedStatus={clientTypeStatus}
            onStatusChange={setClientTypeStatus}
            targetMonth={Number(targetMonth)}
          />

          <AnnualStatTable
            title={`分野別年間実績表（${yearLabel}）`}
            firstColumnLabel="分野"
            rows={industryRes?.data?.rows ?? []}
            isLoading={industryLoading}
            statusOptions={STATUS_OPTIONS}
            selectedStatus={industryStatus}
            onStatusChange={setIndustryStatus}
            targetMonth={Number(targetMonth)}
          />
        </Stack>
      </InnerBox>
    </Stack>

      <FooterContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="ghost" size="sm" onClick={() => navigate(AppRoutes.home)}>
            戻る
          </Button>
          <Button variant="outline" size="sm" startIcon={<IconFileExport />} onClick={handleExportCsv} disabled={isExporting}>
            CSV出力
          </Button>
        </Stack>
      </FooterContainer>
    </>
  );
};

const SwitchWrapper = styled(Box)`
  .MuiFormControlLabel-root {
    flex-direction: row-reverse;
    margin-left: 0;
    margin-right: 0;
    gap: 8px;
  }

  .MuiTypography-root {
    font-size: 12px;
  }
`;
